import { graphql } from "@acto/gql";
import { PaginatedDataAccessResult, PagingOptions } from "@acto/taskpane/core/data-access/types";
import { useMemo } from "react";
import { GetChatHistoriesQuery } from "@acto/gql/graphql";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { ChatHistory } from "@acto/taskpane/core/domain";

const ChatHistoriesQuery = graphql(`
  query GetChatHistories($first: Int!, $after: String, $filter: ChatHistoryFilter!) {
    chatHistories(first: $first, after: $after, filter: $filter) {
      __typename
      edges {
        __typename
        cursor
        node {
          id
          __typename
          client {
            id
            __typename
            name
            externalReference
          }
          sessionId
          messages
          createdAt
          updatedAt
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

interface ChatHistoryFilterOptions {
  customerId: string;
  sessionId?: string;
}

export function useChatHistories({
  customerId,
  sessionId,
  limit = 20,
}: PagingOptions & ChatHistoryFilterOptions): PaginatedDataAccessResult<ChatHistory> {
  const filter = useMemo(
    () => ({
      clientId: customerId,
      sessionId,
    }),
    [customerId, sessionId],
  );

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter, refetch } = usePagination({
    query: ChatHistoriesQuery,
    limit,
    connection: (data: GetChatHistoriesQuery) => data.chatHistories,
    variables: { filter },
  });

  const chatHistories = useMemo(() => data?.map(ChatHistory.fromGraphql), [data]);

  return {
    data: chatHistories,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
    refetch,
  };
}
